// @file The store for magic padlet panel displayed by DashLayoutPickerPage.vue and LayoutPickerDesktop.vue
import device from '@@/bits/device'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { getNextPrompt, getRoleAndRandomPrompt, GRADES, isExamplePrompt } from '@@/bits/magic_padlet_helper'
import { poll } from '@@/bits/polling'
import type { PopoverAnchor } from '@@/library/v4/components/OzPopoverModal.vue'
import PadletApi from '@@/surface/padlet_api'
import type { LibraryId, MagicTemplate, MagicWallCreationResult, MagicWallCreationStatusResult } from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { FetchError, HTTPMethod } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum MagicPadletPanelStatus {
  Initial = 'Initial',
  Creating = 'Creating',
  Errored = 'Errored',
  AtQuota = 'AtQuota',
}

const magicTemplateEnum: Record<string, MagicTemplate> = {
  LessonPlan: {
    name: __('Lesson plan'),
    key: 'lesson_plan',
  },
  MapOfHistoricalEvents: {
    name: __('Map of historical events'),
    key: 'map_of_historical_events',
  },
  ReadingList: {
    name: __('Reading list'),
    key: 'reading_list',
  },
  ClassroomActivitiesList: {
    name: __('Classroom activities list'),
    key: 'classroom_activity_ideas',
  },
  CustomBoard: {
    name: __('Custom board'),
    key: 'custom_board',
  },
}

export const useMagicPadletPanelStore = defineStore('magicPadletPanelStore', () => {
  /**
   * Magic Templates Fields
   */
  const role = ref(__('teacher'))
  const prompt = ref('')
  const placeholder = ref('')
  const subject = ref('')
  const lessonObjectives = ref('')
  const grades = ref<string[]>([])
  const additionalDetails = ref('')
  const classroomResources = ref('')
  const timePeriod = ref('')
  const geographicalFocus = ref('')
  const topic = ref('')

  const gradesSelectedMap = computed(() => {
    return GRADES.reduce((selected, grade) => {
      selected[grade] = grades.value.includes(grade)
      return selected
    }, {})
  })

  function singleSelectGrade(grade: string): void {
    if (grades.value.includes(grade)) {
      grades.value = []
      return
    }
    grades.value = [grade]
  }

  function updateSelectedGrades(grade: string): void {
    if (grades.value.includes(grade)) {
      grades.value = grades.value.filter((selectedGrade) => selectedGrade !== grade)
    } else {
      grades.value.push(grade)
    }
  }

  function resetFields(): void {
    subject.value = ''
    lessonObjectives.value = ''
    grades.value = []
    additionalDetails.value = ''
    classroomResources.value = ''
    timePeriod.value = ''
    geographicalFocus.value = ''
    topic.value = ''
  }

  /**
   * Validation
   */
  function isFieldEmpty(field: string): boolean {
    return field.trim().length === 0
  }

  function isGradesEmpty(grades: string[]): boolean {
    return grades.length === 0
  }

  const isMagicTemplateValid = computed(() => {
    return !isFieldEmpty(subject.value) && !isGradesEmpty(grades.value) && !isFieldEmpty(lessonObjectives.value)
  })

  const isCustomBoardValid = computed(() => {
    return !isFieldEmpty(role.value) && !isFieldEmpty(prompt.value)
  })

  const isReadingListValid = computed(() => {
    return !isFieldEmpty(subject.value) && !isGradesEmpty(grades.value) && !isFieldEmpty(topic.value)
  })

  const isFormValid = computed(() => {
    if (
      magicTemplateSelected.value === magicTemplateEnum.LessonPlan ||
      magicTemplateSelected.value === magicTemplateEnum.ClassroomActivitiesList ||
      magicTemplateSelected.value === magicTemplateEnum.MapOfHistoricalEvents
    ) {
      return isMagicTemplateValid.value
    } else if (magicTemplateSelected.value === magicTemplateEnum.CustomBoard) {
      return isCustomBoardValid.value
    } else if (magicTemplateSelected.value === magicTemplateEnum.ReadingList) {
      return isReadingListValid.value
    } else {
      return false
    }
  })

  /**
   * Magic Template Selector
   */
  const xMagicTemplateSelector = ref(false)
  const magicTemplateSelected = ref<MagicTemplate>(magicTemplateEnum.CustomBoard)
  const magicTemplatesArray = ref<MagicTemplate[]>(Object.values(magicTemplateEnum))
  const isLessonPlanView = computed(() => magicTemplateSelected.value === magicTemplateEnum.LessonPlan)

  function toggleMagicTemplateSelector(): void {
    xMagicTemplateSelector.value = !xMagicTemplateSelector.value
  }

  function updateMagicTemplateSelected(payload: { magicTemplateSelected: MagicTemplate }): void {
    magicTemplateSelected.value = payload.magicTemplateSelected
    xMagicTemplateSelector.value = !xMagicTemplateSelector.value
    resetFields()
  }

  /**
   * Magic Template Selector Anchor
   */
  const magicTemplateSelectorAnchor = ref<Partial<PopoverAnchor>>({})
  function updateMagicTemplateSelectorAnchor(anchor: Partial<PopoverAnchor>): void {
    magicTemplateSelectorAnchor.value = anchor
  }

  /**
   * Creation
   */
  const status = ref(MagicPadletPanelStatus.Initial)
  const isCreatingMagicPadletPanel = computed(() => status.value === MagicPadletPanelStatus.Creating)
  const isAtQuota = computed(() => status.value === MagicPadletPanelStatus.AtQuota)
  const index = ref(0)
  const isExample = ref(false)
  const includeImages = ref(true)

  function generateRandomRolePromptCombination(): void {
    // This happens when user clicks on 'Example' button
    // Iterate from the current index until the end
    const nextPromptAndIndex = getNextPrompt(index.value)
    prompt.value = nextPromptAndIndex.prompt
    index.value = nextPromptAndIndex.index
  }

  async function generateMagicPadlet({
    currentLibraryId,
    includeImages,
    magicTemplateKey,
  }: {
    currentLibraryId: LibraryId | undefined
    includeImages: boolean
    magicTemplateKey: string
  }): Promise<void> {
    try {
      status.value = MagicPadletPanelStatus.Creating
      if (isExamplePrompt(prompt.value)) {
        isExample.value = true
      }
      const response: JsonAPIResponse<MagicWallCreationResult> = await PadletApi.Wall.createMagic({
        role: role.value,
        userPrompt: prompt.value,
        currentLibraryId,
        isExample: isExample.value,
        includeImages,
        magicTemplateKey,
        subject: subject.value,
        lessonObjectives: lessonObjectives.value,
        grades: grades.value.join(', '),
        additionalDetails: additionalDetails.value,
        classroomResources: classroomResources.value,
        timePeriod: timePeriod.value,
        geographicalFocus: geographicalFocus.value,
        topic: topic.value,
      })
      const statusLink = (response?.data as JsonAPIResource<MagicWallCreationResult>)?.attributes.url

      // Query the status url until it gets us a response, then use the response.
      const { data }: JsonAPIResponse<MagicWallCreationStatusResult> = await poll({
        pollingUrl: statusLink,
        validationCallback: ({ data }) => data?.attributes?.status !== 'in_progress', // If there is no data, this validation will fail too
        options: { intervalSecs: 5, maxAttempts: 60, method: HTTPMethod.get },
      })

      const statusResult = (data as JsonAPIResource<MagicWallCreationStatusResult>)?.attributes

      switch (statusResult?.status) {
        case 'at_quota':
          status.value = MagicPadletPanelStatus.AtQuota
          break
        case 'failed':
          status.value = MagicPadletPanelStatus.Errored
          break
      }

      if (statusResult.status !== 'success') {
        const errorMessage = `Magic wall creation failed${statusResult.status === 'at_quota' ? ' due to quota' : ''}`
        const e = new Error(errorMessage)
        captureException(e)
      }
      // TODO: dedup with the logic in the `layoutPicker` store.
      // To show the onboarding flow when user first visit the padlet
      navigateTo(statusResult.wall_url, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        closeMagicPadletPanel()
      }
    } catch (e) {
      status.value = MagicPadletPanelStatus.Errored
      if (e instanceof FetchError) {
        captureFetchException(e, { source: 'MagicWallCreation' })
      } else {
        captureException(e)
      }
    }
  }

  function flipImageToggle(): void {
    includeImages.value = !includeImages.value
  }

  /**
   * Panel Actions
   */
  const xMagicPadletPanel = ref(false)

  function openMagicPadletPanel(): void {
    const randomPrompt = getRoleAndRandomPrompt()
    xMagicPadletPanel.value = true
    role.value = 'teacher'
    prompt.value = ''
    placeholder.value = randomPrompt.prompt
    index.value = randomPrompt.index
    status.value = MagicPadletPanelStatus.Initial
    isExample.value = false
    subject.value = ''
    lessonObjectives.value = ''
    additionalDetails.value = ''
    classroomResources.value = ''
    timePeriod.value = ''
    geographicalFocus.value = ''
    grades.value = []
  }

  function closeMagicPadletPanel(): void {
    xMagicPadletPanel.value = false
  }

  return {
    /**
     * Magic Templates Fields
     */
    role,
    prompt,
    placeholder,
    isLessonPlanView,
    subject,
    lessonObjectives,
    grades,
    additionalDetails,
    classroomResources,
    timePeriod,
    geographicalFocus,
    topic,
    gradesSelectedMap,
    updateSelectedGrades,
    singleSelectGrade,

    /**
     * Magic Template Selector
     */
    xMagicTemplateSelector,
    magicTemplateSelected,
    magicTemplatesArray,
    magicTemplateSelectorAnchor,
    toggleMagicTemplateSelector,
    updateMagicTemplateSelected,
    updateMagicTemplateSelectorAnchor,

    /**
     * Validation
     */
    isFormValid,

    /**
     * Creation
     */
    index,
    status,
    isExample,
    includeImages,
    isCreatingMagicPadletPanel,
    isAtQuota,
    generateRandomRolePromptCombination,
    generateMagicPadlet,
    flipImageToggle,

    /**
     * Panel Actions
     */
    xMagicPadletPanel,
    openMagicPadletPanel,
    closeMagicPadletPanel,
  }
})

export { MagicPadletPanelStatus, magicTemplateEnum }
